import * as React from 'react';
import RoutesList from '../RoutesList/RoutesList';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { memo, useState } from 'react';
import styled from '@emotion/styled';
import { useFeatures } from '@paralleldrive/react-feature-toggles';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import MapboxLanguage from '@mapbox/mapbox-gl-language';

import ReactMapGL, {
    AttributionControl,
    FlyToInterpolator,
    FullscreenControl,
    Layer,
    Marker,
    NavigationControl,
    Popup,
    Source,
    WebMercatorViewport,
} from 'react-map-gl';
import { MapMarker } from '../../pages/Cruise/components/MapMarker';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useActions } from '../../hooks/useActions';

const ImageHolder = styled.div`
    width: 100%;
    margin-top: 36px;
    padding-right: 15px;
    .route-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

type IWaypoint = {
    arrival: string;
    city: string;
    day: number;
    departure: string;
    description: string;
    image: string;
    info: string[];
    isPortOfDisembarkation: boolean;
    isPortOfEmbarkation: boolean;
    lat: number;
    long: number;
    nid: number;
    title: string;
};

const applyToArray = (func, array) => func.apply(Math, array);

const getBoundsForPoints = (waypoints: IWaypoint[], width: number, height: number) => {
    const pointsLong = waypoints.map((point) => point.long);
    const pointsLat = waypoints.map((point) => point.lat);
    const cornersLongLat = [
        [applyToArray(Math.min, pointsLong), applyToArray(Math.min, pointsLat)],
        [applyToArray(Math.max, pointsLong), applyToArray(Math.max, pointsLat)],
    ] as [[number, number], [number, number]];
    const viewport = new WebMercatorViewport({
        width: width,
        height: height,
    }).fitBounds(cornersLongLat, { padding: 100 });
    const { longitude, latitude, zoom } = viewport;
    return { longitude, latitude, zoom };
};

const RoutesMapList = () => {
    const { cruisePage, pageData } = useTypedSelector((state) => state);
    const { routeImage, waypoints } = cruisePage;
    const features = useFeatures();
    const [selectedWaypoint, toggleSelectedWaypoint] = useState<IWaypoint | null>(null);
    const bounds = getBoundsForPoints(
        waypoints.filter((waypoint) => waypoint.long && waypoint.lat),
        550,
        500,
    );
    const [viewport, setViewPort] = React.useState({
        ...bounds,
        maxZoom: 15,
        width: '100%',
        height: '100%',
        transitionDuration: 750,
        transitionInterpolator: new FlyToInterpolator(),
    });
    const { getRoute } = useActions();
    const mapRef = useRef(null);

    useEffect(() => {
        if (mapRef && mapRef.current) {
            const map = mapRef.current.getMap();
            map.addControl(new MapboxLanguage({ defaultLanguage: pageData.appConfig.countryCode }));
        }
    }, [mapRef]);

    useEffect(() => {
        getRoute();
        let elm = document.querySelector<HTMLElement>('#cruiseRouteMap > div');
        if (elm) {
            elm.style.height = '100vh';
        }
    }, []);

    const { route } = cruisePage;

    const sendEventClick = () => {
        eventTracker.track({
            category: 'cruisepage-infobox',
            action: 'itinerary image',
            label: cruisePage.title,
        });
    };

    const changeViewPort = (newViewport) => {
        if (viewport.width === '100%' || viewport.width === newViewport.width) {
            setViewPort({
                ...newViewport,
                maxZoom: 15,
            });
        } else {
            const bounds = getBoundsForPoints(
                waypoints.filter((waypoint) => waypoint.long && waypoint.lat),
                newViewport.width,
                newViewport.height,
            );
            setViewPort({
                width: newViewport.width,
                height: newViewport.height,
                ...bounds,
                maxZoom: 15,
                transitionDuration: 1500,
                transitionInterpolator: new FlyToInterpolator(),
            });
        }
    };

    const clickMarker = (waypoint) => {
        const latitude =
            viewport.zoom >= 1
                ? waypoint.lat + 100 / Math.pow(viewport.zoom, 4)
                : waypoint.lat + 100 * Math.pow(viewport.zoom, 4);
        changeViewPort({
            ...viewport,
            longitude: waypoint.long,
            latitude,
            transitionDuration: 750,
            transitionInterpolator: new FlyToInterpolator(),
        });
        toggleSelectedWaypoint(waypoint);
    };

    const getMarkers = (waypoints: IWaypoint[], isOnlyPoint: boolean) => {
        return (
            waypoints?.length &&
            waypoints.map((waypoint: IWaypoint, index: number) => {
                if (!waypoint.long || !waypoint.lat) {
                    return;
                }
                const firstDestination = index === 0;
                const lastDestination = index === waypoints.length - 1;
                const styleMarker = `marker-html ${
                    firstDestination || lastDestination ? 'highlighted' : ''
                }`;
                return (
                    <div className={styleMarker} key={index + 1}>
                        <Marker
                            offsetTop={
                                isOnlyPoint && index !== 0 && index !== waypoints.length - 1
                                    ? 0
                                    : -39
                            }
                            offsetLeft={
                                isOnlyPoint && index !== 0 && index !== waypoints.length - 1
                                    ? 0
                                    : -15
                            }
                            latitude={waypoint.lat}
                            longitude={waypoint.long}
                            className={`${
                                selectedWaypoint && selectedWaypoint.nid === waypoint.nid
                                    ? 'active'
                                    : ''
                            } ${
                                isOnlyPoint
                                    ? index === 0 || index === waypoints.length - 1
                                        ? 'only_point_anchor'
                                        : 'only_point'
                                    : ''
                            }`}
                            onClick={() => clickMarker(waypoint)}
                        >
                            {!isOnlyPoint && (
                                <MapMarker
                                    index={waypoint.day}
                                    lng={waypoint.long}
                                    {...waypoint}
                                    zoom={viewport.zoom}
                                    first={firstDestination || lastDestination}
                                />
                            )}
                        </Marker>
                    </div>
                );
            })
        );
    };

    const navControlStyle: React.CSSProperties = {
        right: 10,
        bottom: 10,
        zIndex: 2,
    };

    const attributionControlStyle: React.CSSProperties = {
        right: 0,
        top: 0,
        zIndex: 2,
    };

    const fullscreenControlStyle: React.CSSProperties = {
        left: 10,
        top: 10,
        zIndex: 2,
    };

    const getLayers = (waypoints: IWaypoint[]): GeoJSON.Feature[] => {
        return (
            waypoints?.length &&
            waypoints
                .filter((waypoint: IWaypoint) => waypoint.long && waypoint.lat)
                .map((waypoint: IWaypoint, index: number) => {
                    return {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [waypoint.long, waypoint.lat] },
                        properties: { name: waypoint.city },
                    } as GeoJSON.Feature;
                })
        );
    };

    const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: getLayers(waypoints),
    };

    const getWaypoints = (i: number) => {
        const data = [];
        if (i > 0) {
            data.push(route.longlat[i - 1][route.longlat[i - 1].length - 1]);
        }
        route.longlat[i].forEach((element) => {
            data.push(element);
        });
        return data;
    };

    const getLineWaypoints = (i: number): GeoJSON.Feature => {
        return {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: getWaypoints(i),
            },
        };
    };

    return (
        <>
            <div id="routes-container">
                {route && (
                    <div id="cruiseRouteMap">
                        {features.includes('showMapBox') && (
                            <ReactMapGL
                                ref={mapRef}
                                {...viewport}
                                scrollZoom={false}
                                mapStyle={
                                    'mapbox://styles/sabinelettermann/cjrtfdfsd2sdb2soz3vmnlc8x'
                                }
                                mapboxApiAccessToken={
                                    'pk.eyJ1Ijoic2FiaW5lbGV0dGVybWFubiIsImEiOiJjanJoeGYwZzMwM2IyM3lvejNlYzFzeGVpIn0.IjsF3jfYGsIuu1Mc9bIriA'
                                }
                                doubleClickZoom={true}
                                attributionControl={false}
                                onViewportChange={changeViewPort}
                            >
                                {selectedWaypoint && (
                                    <Popup
                                        className="map-popUp"
                                        anchor="bottom"
                                        offsetTop={
                                            route === null ||
                                            (route !== null &&
                                                (selectedWaypoint === waypoints[0] ||
                                                    selectedWaypoint ===
                                                        waypoints[waypoints.length - 1]))
                                                ? -40
                                                : -8
                                        }
                                        closeButton={true}
                                        closeOnClick={false}
                                        dynamicPosition={false}
                                        latitude={selectedWaypoint.lat}
                                        longitude={selectedWaypoint.long}
                                        onClose={() => toggleSelectedWaypoint(null)}
                                    >
                                        <div className="map-popUp__content">
                                            <img
                                                className="map-popUp__image"
                                                src={selectedWaypoint.image}
                                                alt={selectedWaypoint.title}
                                            />
                                            <div>
                                                <h3>{selectedWaypoint.title}</h3>
                                                {selectedWaypoint.description}
                                            </div>
                                        </div>
                                    </Popup>
                                )}
                                {route === null ? (
                                    getMarkers(waypoints, false)
                                ) : (
                                    <>
                                        {route.longlat.map((longlat, i) => (
                                            <Source
                                                key={i}
                                                type="geojson"
                                                data={getLineWaypoints(i)}
                                            >
                                                <Layer
                                                    type="line"
                                                    layout={{
                                                        'line-join': 'round',
                                                        'line-cap': 'round',
                                                    }}
                                                    paint={{
                                                        'line-color': 'black',
                                                        'line-width': 1,
                                                    }}
                                                />
                                                <Layer
                                                    type="symbol"
                                                    layout={{
                                                        'symbol-placement': 'line-center',
                                                        'symbol-spacing': 1,
                                                        'icon-allow-overlap': false,
                                                        'icon-rotate': 90,
                                                        'icon-image': 'triangle-11',
                                                        'icon-size': 1,
                                                        visibility: 'visible',
                                                    }}
                                                    paint={{}}
                                                />
                                            </Source>
                                        ))}
                                        {getMarkers(waypoints, true)}
                                    </>
                                )}
                                <Source type="geojson" data={geojson}>
                                    <Layer
                                        type="symbol"
                                        paint={{
                                            'text-color': '#202',
                                            'text-halo-color': '#fff',
                                            'text-halo-width': 2,
                                        }}
                                        layout={{
                                            'icon-size': 1,
                                            'text-size': 11,
                                            'icon-rotate': 180,
                                            'text-anchor': 'top',
                                            'text-field': '{name}',
                                            'text-offset': [0, 0.25],
                                            'icon-allow-overlap': false,
                                            'text-allow-overlap': false,
                                            'text-letter-spacing': 0.05,
                                            'text-transform': 'uppercase',
                                            'icon-ignore-placement': false,
                                            'text-ignore-placement': false,
                                            'text-font': [
                                                'Open Sans Bold',
                                                'Arial Unicode MS Bold',
                                            ],
                                        }}
                                    />
                                </Source>
                                <div id="mapbox-controls">
                                    <AttributionControl
                                        compact={true}
                                        style={attributionControlStyle}
                                    />
                                    <NavigationControl
                                        style={navControlStyle}
                                        showCompass={false}
                                        onViewportChange={changeViewPort}
                                    />
                                    <FullscreenControl
                                        style={fullscreenControlStyle}
                                        className="isFullScreen"
                                    />
                                </div>
                            </ReactMapGL>
                        )}
                        {features.includes('showStaticImagesInRouteSection') && !!routeImage && (
                            <ImageHolder onClick={() => sendEventClick()}>
                                <img className="route-image" src={routeImage} alt="Route" />
                            </ImageHolder>
                        )}
                    </div>
                )}
                <div>
                    <RoutesList waypoints={waypoints} />
                </div>
            </div>
        </>
    );
};
export default memo(RoutesMapList);
